/**
 * Fonctions de validation réutilisables pour le système de booking
 */

/**
 * Valide qu'une chaîne est un UUID v4 valide
 * 
 * @param uuid - Chaîne à valider
 * @returns true si l'UUID est valide
 * 
 * @example
 * isValidUUID('550e8400-e29b-41d4-a716-446655440000') // true
 * isValidUUID('invalid-uuid') // false
 */
export function isValidUUID(uuid: string): boolean {
  if (!uuid || typeof uuid !== 'string') {
    return false;
  }

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}

/**
 * Valide qu'une chaîne est une date ISO 8601 valide
 * 
 * @param dateString - Chaîne de date à valider
 * @returns true si la date est valide et au format ISO 8601
 * 
 * @example
 * isValidISODate('2025-01-15T10:00:00Z') // true
 * isValidISODate('2025-01-15T10:00:00.000Z') // true
 * isValidISODate('invalid-date') // false
 */
export function isValidISODate(dateString: string): boolean {
  if (!dateString || typeof dateString !== 'string') {
    return false;
  }

  const date = new Date(dateString);
  
  // Vérifie que la date est valide
  if (isNaN(date.getTime())) {
    return false;
  }

  // Vérifie que la chaîne est bien au format ISO 8601
  // La conversion aller-retour doit donner la même chaîne (avec tolérance pour les millisecondes)
  const isoString = date.toISOString();
  return dateString === isoString || dateString.startsWith(isoString.slice(0, -5));
}

/**
 * Valide qu'une date n'est pas dans le passé
 * 
 * @param date - Date à valider
 * @returns true si la date est future ou présente
 * 
 * @example
 * isFutureDate(new Date('2099-01-01')) // true
 * isFutureDate(new Date('2020-01-01')) // false
 */
export function isFutureDate(date: Date): boolean {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return false;
  }

  return date.getTime() >= Date.now();
}

/**
 * Valide qu'un email est au format correct
 * 
 * @param email - Email à valider
 * @returns true si l'email est valide
 * 
 * @example
 * isValidEmail('user@example.com') // true
 * isValidEmail('invalid-email') // false
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== 'string') {
    return false;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Valide les paramètres d'un booking
 * 
 * @param params - Paramètres à valider
 * @returns Objet avec valid et message d'erreur optionnel
 * 
 * @example
 * const validation = validateBookingParams({
 *   userId: 'uuid',
 *   startTime: '2025-01-15T10:00:00Z',
 *   eventTypeSlug: 'shooting-first'
 * });
 * 
 * if (!validation.valid) {
 *   console.error(validation.error);
 * }
 */
export function validateBookingParams(params: {
  userId?: string;
  startTime?: string;
  endTime?: string;
  eventTypeSlug?: string;
  attendeeEmail?: string;
  attendeeName?: string;
}): { valid: boolean; error?: string } {
  if (!params.userId) {
    return { valid: false, error: 'Missing required field: userId' };
  }

  if (!isValidUUID(params.userId)) {
    return { valid: false, error: 'Invalid userId format (must be UUID)' };
  }

  if (!params.startTime) {
    return { valid: false, error: 'Missing required field: startTime' };
  }

  if (!isValidISODate(params.startTime)) {
    return { valid: false, error: 'Invalid startTime format (must be ISO 8601)' };
  }

  if (params.endTime && !isValidISODate(params.endTime)) {
    return { valid: false, error: 'Invalid endTime format (must be ISO 8601)' };
  }

  if (!params.eventTypeSlug || params.eventTypeSlug.trim().length === 0) {
    return { valid: false, error: 'Missing required field: eventTypeSlug' };
  }

  if (params.attendeeEmail && !isValidEmail(params.attendeeEmail)) {
    return { valid: false, error: 'Invalid attendeeEmail format' };
  }

  if (params.attendeeName && params.attendeeName.trim().length === 0) {
    return { valid: false, error: 'attendeeName cannot be empty' };
  }

  return { valid: true };
}

/**
 * Valide les paramètres de staff assignment
 * 
 * @param params - Paramètres à valider
 * @returns Objet avec valid et message d'erreur optionnel
 */
export function validateStaffAssignmentParams(params: {
  customerId?: string;
  directorId?: string;
  pbmId?: string;
}): { valid: boolean; error?: string } {
  if (!params.customerId) {
    return { valid: false, error: 'customerId is required' };
  }

  if (!isValidUUID(params.customerId)) {
    return { valid: false, error: 'Invalid customerId format (must be UUID)' };
  }

  if (params.directorId && !isValidUUID(params.directorId)) {
    return { valid: false, error: 'Invalid directorId format (must be UUID)' };
  }

  if (params.pbmId && !isValidUUID(params.pbmId)) {
    return { valid: false, error: 'Invalid pbmId format (must be UUID)' };
  }

  return { valid: true };
}
